// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';

import LogIn from './auth/LogIn';
import { authService } from './apiServices/authService';
import Branch from './pages/setting/Branch';
import Company from './pages/setting/Company';
import Permission from './pages/setting/Permission';
import Users from './pages/setting/Users';
import Property from './pages/property/Property';
import FloorUnit from './pages/property/FloorUnit';
import Tenant from './pages/tenant/Tenant';
import AssignTenant from './pages/tenant/AssignTenant';
import PaymentHistory from './pages/payment/PaymentHistory';
import RentReminder from './pages/rent/RentReminder';
import PaymentReceipt from './pages/payment/PaymentReceipt';
import TenantLedger from './pages/tenant/TenantLedger';
import VacancyManagement from './pages/property/VacancyManagement';
import MaintenanceRequest from './pages/property/MaintenanceRequest';
import SecurityDepositTracking from './pages/payment/SecurityDepositTracking';
import MoveOutChecklist from './pages/tenant/MoveOutChecklist';
import ExpenseCategory from './pages/expense/ExpenseCategory';
import ExpenseList from './pages/expense/ExpenseList';
import RentCollectionReport from './pages/report/RentCollectionReport';
import Reminders from './pages/rent/Reminders';
import Dashboard from './pages/main/Dashboard';
import NotFound from './components/NotFound';

const theme = createTheme({
    palette: {
        primary: {
            main: '#3A0519',
            light: '#222833',
        },
        secondary: {
            main: '#2e7d32',
            light: '#FFFFFF',
        },
        success: {
            light: '#4caf50',
            main: '#2e7d32',
            contrastText: '#fff',
        },
        info: {
            light: '#2196f3',
            main: '#1976d2',
            contrastText: '#fff',
        },
        background: {
            default: '#f5f5f5',
            paper: '#fff'
        },
        text:{
            secondary:'#2e7d32',
            primary: '#1976d2',

        },
        bodyBg:{
            main:"#354F52",
            navi:"#004B60",
            headerNavi:"#003049",
        },
    },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
    if (!authService.isAuthenticated()) {
        return <Navigate to="/" replace />;
    }
    return children;
};

function App() {
    return (
        <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router>
                    <Routes>
                        {/* Public route */}
                        <Route path="/" element={<LogIn />} />

                        {/* Protected routes with Layout */}
                        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />

                        {/* Properties */}
                        <Route path="/properties" element={<ProtectedRoute><Layout><Property /></Layout></ProtectedRoute>} />
                        <Route path="/floor-units" element={<ProtectedRoute><Layout><FloorUnit /></Layout></ProtectedRoute>} />
                        <Route path="/vacancy" element={<ProtectedRoute><Layout><VacancyManagement /></Layout></ProtectedRoute>} />
                        <Route path="/maintenance" element={<ProtectedRoute><Layout><MaintenanceRequest /></Layout></ProtectedRoute>} />

                        {/* Tenants */}
                        <Route path="/tenants" element={<ProtectedRoute><Layout><Tenant /></Layout></ProtectedRoute>} />
                        <Route path="/assign-tenant" element={<ProtectedRoute><Layout><AssignTenant /></Layout></ProtectedRoute>} />
                        <Route path="/tenant-ledger" element={<ProtectedRoute><Layout><TenantLedger /></Layout></ProtectedRoute>} />
                        <Route path="/move-out" element={<ProtectedRoute><Layout><MoveOutChecklist /></Layout></ProtectedRoute>} />

                        {/* Payments */}
                        <Route path="/payment-history" element={<ProtectedRoute><Layout><PaymentHistory /></Layout></ProtectedRoute>} />
                        <Route path="/payment-receipts" element={<ProtectedRoute><Layout><PaymentReceipt /></Layout></ProtectedRoute>} />
                        <Route path="/security-deposits" element={<ProtectedRoute><Layout><SecurityDepositTracking /></Layout></ProtectedRoute>} />

                        {/* Rent */}
                        {/* <Route path="/rent-roll" element={<ProtectedRoute><Layout><RentReminder /></Layout></ProtectedRoute>} /> */}
                        <Route path="/rent-reminders" element={<ProtectedRoute><Layout><RentReminder /></Layout></ProtectedRoute>} />
                        {/* <Route path="/reminders" element={<ProtectedRoute><Layout><Reminders /></Layout></ProtectedRoute>} /> */}

                        {/* Reports */}
                        <Route path="/reports" element={<ProtectedRoute><Layout><RentCollectionReport /></Layout></ProtectedRoute>} />

                        {/* Settings */}
                        <Route path="/branch" element={<ProtectedRoute><Layout><Branch /></Layout></ProtectedRoute>} />
                        <Route path="/company" element={<ProtectedRoute><Layout><Company /></Layout></ProtectedRoute>} />
                        <Route path="/permission" element={<ProtectedRoute><Layout><Permission /></Layout></ProtectedRoute>} />
                        <Route path="/users" element={<ProtectedRoute><Layout><Users /></Layout></ProtectedRoute>} />

                        {/* Expense */}
                        <Route path="/expenses" element={<ProtectedRoute><Layout><ExpenseList /></Layout></ProtectedRoute>} />
                        <Route path="/expense-categories" element={<ProtectedRoute><Layout><ExpenseCategory /></Layout></ProtectedRoute>} />

                        {/* 404 Not Found - Protected with Layout */}
                        <Route path="*" element={
                            <ProtectedRoute>
                                <Layout>
                                    <NotFound />
                                </Layout>
                            </ProtectedRoute>
                        } />
                    </Routes>
                </Router>
            </ThemeProvider>
        </SnackbarProvider>
    );
}

export default App;